import React, {
    createContext,
    useContext,
    useReducer,
    ReactNode,
    useEffect,
    useCallback,
} from 'react';
import {
    extractIdsFromLocation,
    concatenateIdToPath,
    assignConfigToItems,
} from '@/utils';
import { AuthenticationContext } from '@/components/provider/AuthenticationProvider';
import {
    getFoyerServiceStore,
    setFoyerServiceStore,
    removeFoyerServiceStore,
} from '@/store';
import type { Location } from 'react-router-dom';
import type { Realm as RealmFields } from '@/generated/graphql';

const FoyerContext = createContext(null);
const FoyerDispatchContext = createContext(null);
interface FoyerProviderProps {
    children: ReactNode;
    initialNav?: {
        realms: RealmFields[];
        location: Location;
    };
}

const DEFAULT_STATE = {
    initialized: false,
    showNav: false,
    showService: false,
    realms: [],
    zoneGroups: [],
    matrixGroups: [],
    activeRealmItem: null,
    activeZoneItem: null,
    activeServiceMenu: null,
    activeServiceMenuTitle: null,
};

function buildNavInitializedState(baseState, realms, location) {
    const locationIds = extractIdsFromLocation(location.pathname);

    const activeItems = getActiveNavAreaItems(
        realms,
        locationIds.realmId,
        locationIds.zoneId,
    );

    const navItemClassNames = ['red', 'green', 'blue', 'yellow'];
    const zoneGroups = [];
    const matrixGroups = [];

    realms.forEach((realm) => {
        const realmPath = concatenateIdToPath({
            id: realm.id,
            pathId: realm.pathId,
        });

        zoneGroups.push({
            parentId: realm.id,
            parentPath: realmPath,
            items: assignConfigToItems(realm.zones, navItemClassNames),
        });

        realm.zones.forEach((zone) => {
            const zonePath = concatenateIdToPath({
                id: zone.id,
                pathId: zone.pathId,
            });

            matrixGroups.push({
                parentId: zone.id,
                parentPath: `${realmPath}/${zonePath}`,
                items: assignConfigToItems(zone.matrices, navItemClassNames),
            });
        });
    });

    return {
        ...baseState,
        initialized: true,
        realms: assignConfigToItems(realms, navItemClassNames),
        zoneGroups,
        matrixGroups,
        activeRealmItem: activeItems.realm,
        activeZoneItem: activeItems.zone,
    };
}

function initFoyerState({
    initialNav,
}: {
    initialNav?: { realms: any[]; location: Location };
}) {
    const realms = initialNav?.realms ?? [];
    const location = initialNav?.location;

    if (!location || realms.length === 0) return DEFAULT_STATE;

    return buildNavInitializedState(DEFAULT_STATE, realms, location);
}

const FoyerProvider = ({ children, initialNav }: FoyerProviderProps) => {
    const [foyerConfig, dispatch] = useReducer(
        foyerConfigReducer,
        { initialNav },
        initFoyerState,
    );

    const [accessToken] = useContext(AuthenticationContext);

    useEffect(() => {
        if (!accessToken) {
            removeFoyerServiceStore();
            dispatch({ type: 'RESET_SERVICE_UI' });
            return;
        }

        const stored = getFoyerServiceStore();
        if (stored) {
            dispatch({ type: 'INITIALIZE_SERVICE_UI', payload: stored });
        }
    }, [accessToken]);

    const dispatchWithAuth = useCallback(
        (action: any) => dispatch({ ...action, accessToken }),
        [accessToken],
    );

    useEffect(() => {
        if (foyerConfig.initialized) return;

        const realms = initialNav?.realms ?? [];
        const location = initialNav?.location;
        if (!location || realms.length === 0) return;

        dispatch({
            type: 'INITIALIZE_NAV_AREAS',
            realms,
            location,
        });
    }, [foyerConfig.initialized, initialNav, dispatch]);

    return (
        <FoyerContext.Provider value={foyerConfig}>
            <FoyerDispatchContext.Provider value={dispatchWithAuth}>
                {children}
            </FoyerDispatchContext.Provider>
        </FoyerContext.Provider>
    );
};

const getActiveNavAreaItems = (realms, realmId, zoneId) => {
    const items = { realm: null, zone: null };

    if (realmId) {
        items.realm = realms.filter((realm) => realm.id === realmId)[0];
    }

    if (items.realm && zoneId) {
        items.zone = items.realm.zones.filter((zone) => zone.id === zoneId)[0];
    }

    return items;
};

const foyerConfigReducer = (foyerConfig, action) => {
    switch (action.type) {
        case 'TOGGLE_NAV': {
            return {
                ...foyerConfig,
                showNav: !foyerConfig.showNav,
                showService: false,
            };
        }

        case 'TOGGLE_SERVICE': {
            const nextState = {
                ...foyerConfig,
                showService: !foyerConfig.showService,
                showNav: false,
            };

            if (action.accessToken) {
                setFoyerServiceStore({
                    showService: nextState.showService,
                    activeServiceMenu: nextState.activeServiceMenu,
                    activeServiceMenuTitle: nextState.activeServiceMenuTitle,
                });
            } else {
                removeFoyerServiceStore();
            }

            return nextState;
        }

        case 'SET_ACTIVE_SERVICE_MENU': {
            const nextState = {
                ...foyerConfig,
                activeServiceMenu: action.serviceMenu,
                activeServiceMenuTitle: action.serviceMenuTitle,
            };

            if (action.accessToken) {
                setFoyerServiceStore({
                    showService: nextState.showService,
                    activeServiceMenu: nextState.activeServiceMenu,
                    activeServiceMenuTitle: nextState.activeServiceMenuTitle,
                });
            } else {
                removeFoyerServiceStore();
            }

            return nextState;
        }

        case 'INITIALIZE_SERVICE_UI': {
            return {
                ...foyerConfig,
                showService: !!action.payload?.showService,
                activeServiceMenu: action.payload?.activeServiceMenu ?? null,
                activeServiceMenuTitle:
                    action.payload?.activeServiceMenuTitle ?? null,
            };
        }

        case 'RESET_SERVICE_UI': {
            return {
                ...foyerConfig,
                showService: false,
                activeServiceMenu: null,
                activeServiceMenuTitle: null,
            };
        }

        case 'INITIALIZE_NAV_AREAS': {
            const locationIds = extractIdsFromLocation(
                action.location.pathname,
            );

            const activeItems = getActiveNavAreaItems(
                action.realms,
                locationIds.realmId,
                locationIds.zoneId,
            );

            const navItemClassNames = ['red', 'green', 'blue', 'yellow'];
            const zoneGroups = [];
            const matrixGroups = [];
            action.realms.forEach((realm) => {
                const realmPath = concatenateIdToPath({
                    id: realm.id,
                    pathId: realm.pathId,
                });
                zoneGroups.push({
                    parentId: realm.id,
                    parentPath: realmPath,
                    items: assignConfigToItems(realm.zones, navItemClassNames),
                });
                realm.zones.forEach((zone) => {
                    const zonePath = concatenateIdToPath({
                        id: zone.id,
                        pathId: zone.pathId,
                    });
                    matrixGroups.push({
                        parentId: zone.id,
                        parentPath: `${realmPath}/${zonePath}`,
                        items: assignConfigToItems(
                            zone.matrices,
                            navItemClassNames,
                        ),
                    });
                });
            });

            return {
                ...foyerConfig,
                initialized: true,
                realms: assignConfigToItems(action.realms, navItemClassNames),
                zoneGroups: zoneGroups,
                matrixGroups: matrixGroups,
                activeRealmItem: activeItems.realm,
                activeZoneItem: activeItems.zone,
            };
        }

        case 'GO_PREV_NAV_AREAS': {
            return {
                ...foyerConfig,
                activeZoneItem: null,
                ...(!foyerConfig.activeZoneItem
                    ? { activeRealmItem: null }
                    : {}),
            };
        }

        case 'GO_NEXT_NAV_AREAS': {
            const activeItems = getActiveNavAreaItems(
                foyerConfig.realms,
                action.areaName === 'zones'
                    ? action.id
                    : foyerConfig.activeRealmItem.id,
                action.areaName === 'matrices' ? action.id : null,
            );

            return {
                ...foyerConfig,
                activeRealmItem: activeItems.realm,
                activeZoneItem: activeItems.zone,
            };
        }

        case 'GO_CURRENT_LOCATION': {
            if (!foyerConfig.initialized) {
                return foyerConfig;
            }
            const locationIds = extractIdsFromLocation(
                action.location.pathname,
            );

            const activeItems = getActiveNavAreaItems(
                foyerConfig.realms,
                locationIds.realmId,
                locationIds.zoneId,
            );

            return {
                ...foyerConfig,
                activeRealmItem: activeItems.realm,
                activeZoneItem: activeItems.zone,
            };
        }

        default: {
            return foyerConfig;
        }
    }
};

export function useFoyerConfig() {
    return useContext(FoyerContext);
}

export function useFoyerConfigDispatch() {
    return useContext(FoyerDispatchContext);
}

export default FoyerProvider;
